import { ApiUrl } from '@config/apiUrl';
import { endSession, readStoredTokens, storeAuthTokens } from '@lib/auth/session';
import axios, { type AxiosRequestConfig } from 'axios';

export const api = axios.create({
  baseURL: ApiUrl.server_url,
});

const apiProtected = axios.create({
  baseURL: ApiUrl.server_url,
});

type QueueEntry = {
  resolve: () => void;
  reject: (error: unknown) => void;
};

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let failedQueue: QueueEntry[] = [];

const processQueue = (error: unknown): void => {
  failedQueue.forEach((entry) => {
    if (error) entry.reject(error);
    else entry.resolve();
  });
  failedQueue = [];
};

const isUnauthorized = (
  error: unknown
): error is { config: RetryConfig; response: { status: number } } => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return false;
  }
  const response = (error as { response?: { status?: number } }).response;
  const status = response?.status;
  return status === 401 || status === 403;
};

const refreshAccessToken = async (): Promise<void> => {
  const refreshToken = readStoredTokens()?.refreshToken;
  if (!refreshToken) {
    throw new Error('Missing refresh token');
  }
  const { data } = await api.post('v1/auth/refresh-token', { refreshToken });
  if (!storeAuthTokens(data)) {
    throw new Error('Refresh did not return a token');
  }
};

apiProtected.interceptors.request.use((config: AxiosRequestConfig) => {
  const accessToken = readStoredTokens()?.accessToken;
  if (accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }
  return config;
});

apiProtected.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!isUnauthorized(error)) return Promise.reject(error);

    const originalRequest = error.config;
    if (originalRequest._retry) return Promise.reject(error);

    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiProtected(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return refreshAccessToken()
      .then(() => {
        processQueue(null);
        return apiProtected(originalRequest);
      })
      .catch((refreshError: unknown) => {
        processQueue(refreshError);
        endSession();
        return Promise.reject(refreshError);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }
);

export default apiProtected;
